import { useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { useSelfServiceStatus } from '@/lib/self-service/api';
import { GET_STARTED_PATH } from '@/lib/self-service/constants';

import { useConfigInfo } from '../../allauth_auth/index.js';
import { AuthProcess } from '../../lib/allauth.js';
import type { Provider } from '../../types/allauth';
import ProviderList from './ProviderList.tsx';

const INVITE_CODE_PATH = '/signup/code';

export const SignupContent = () => {
  const { providers } = useConfigInfo();
  const [searchParams] = useSearchParams();
  const { data: selfService, isPending: isGatePending } =
    useSelfServiceStatus();
  const hasProviders =
    providers.filter((p: Provider) => p.client_id).length > 0;

  const code = searchParams.get('code');
  const isDemo = searchParams.get('demo') === 'true';

  // Ensure sessionStorage flag is set when demo=true. This handles the case
  // where user navigates directly to /account/signup?demo=true.
  useEffect(() => {
    if (isDemo) {
      sessionStorage.setItem('signupDemo', 'true');
    }
  }, [isDemo]);

  // Without an invite code or demo mode this screen has nothing to offer:
  // self-service signups go to /get-started when the server gate is on,
  // otherwise to the invite-code entry page. Wait for the gate so a visitor
  // is never bounced into the wrong flow first.
  if (!code && !isDemo) {
    if (isGatePending) {
      return null;
    }
    return (
      <Navigate
        to={selfService?.enabled ? GET_STARTED_PATH : INVITE_CODE_PATH}
        replace
      />
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
          Your smartest CRM decision yet
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-400'>
          Sign up with Google to get started
        </p>
      </div>

      {/* Google Signup */}
      <div className='flex flex-col gap-4'>
        {hasProviders && (
          <ProviderList
            callbackURL='/account/provider/callback'
            process={AuthProcess.LOGIN}
          />
        )}
      </div>

      {/* Sign In Link */}
      <div className='flex justify-center gap-1 text-center'>
        <span className='text-sm text-gray-600 dark:text-gray-400'>
          Already have an account?
        </span>
        <Link
          to='/account/login'
          className='text-sm font-semibold text-fg-brand-primary hover:text-utility-brand-700 transition-colors'
        >
          Log in
        </Link>
      </div>
    </div>
  );
};
